import * as _ from 'lodash';
import { Utils } from "../Utils/Utils";
import { DateUtils, DateType } from "../Utils/DateUtils";
import { StringUtils } from "../Utils/StringUtils";
import {WidgetApiBase} from './WidgetBase';

/**
 * Api wrapper for 'bootstrap3-typeahead.js'
 */
export class TypeaheadApi extends WidgetApiBase {
    private _typeahead: JQuery;

    constructor(id: string) {
        super(id);
    }

    protected getInitOptions(): Object {
        return null;
    }

    protected getTypeahead(doInit: boolean = false): JQuery {
        if (!this._typeahead) {
            var opt = doInit ? this.getInitOptions() : undefined;
            this._typeahead = this.jq.typeahead(opt);
        }
        return this._typeahead;
    }

    protected callApi(options: any, value?: any): void {
        this.getTypeahead();
        this.jq.typeahead(options, value);
    }

    public lookup(): void {
        (this.getTypeahead() as any).lookup();
    }

    public getActiveItem(): Object {
        return (this.getTypeahead() as any).getActive();
    }
}

export type TypeaheadSourceBase = Array<string> | Array<{ name: string }>;
export type TypeaheadSourceFunction = (query: string, callback: (items: TypeaheadSourceBase)=> void) => void;
export type TypeaheadSource = TypeaheadSourceBase | TypeaheadSourceFunction;

export type TypeaheadDisplayText = (item: Object) => string;

export type TypeaheadAfterSelectEvent = (item: any) => void;

export interface TypeaheadOptions {
    /**
     * @property source - The data source to query against.
     */
    source: TypeaheadSource;


    /**
     * items - The max number of items to display in the dropdown. Can also be set to 'all'.
     * @default value: 8
     */
    items?: number | 'all';

    /**
     * @property minLength - The minimum character length needed before triggering autocomplete suggestions.
     *  You can set it to 0 so suggestion are shown even when there is no text when lookup function is called.
     * @default value: 1
     */
    minLength?: number;

    /**
     * @property showHintOnFocus - If hints should be shown as soon as the input gets focus.
     * If set to true, all match will be shown. If set to "all", it will display all hints, not filtering them by the current text.
     * This can be used when you want an input that behaves a bit like a combo box plus auto completion as you type to filter the choices.
     * @default value: false
     */
    showHintOnFocus?: boolean | 'all';

    /**
     * @property scrollHeight - Number of pixels the scrollable parent container scrolled down (scrolled out the viewport).
     * @default value: 0
     */
    scrollHeight?: number;

    /**
     * @property displayText - Method used to get textual representation of an item of the sources.
     *  Accepts a single argument item and has the scope of the typeahead instance. Should return a String.
     * @default value: item.name || item
     */
    displayText?: TypeaheadDisplayText;

    /**
     * @property autoSelect - Allows you to dictate whether or not the first suggestion is selected automatically.
     * @default value: true
     */
    autoSelect?: boolean;

    /**
     * @property delay - Adds a delay between lookups.
     * @default value: 0
     */
    delay?: number;

    /**
     * @property fitToElement - Set to true if you want the menu to be the same size than the input it is attached to.
     * @default value: false
     */
    fitToElement?: boolean;

    /**
    * @property afterSelect - Call back function to execute after selected an item. It gets the current active item in parameter if any.
    */
    afterSelect?: TypeaheadAfterSelectEvent;


    // extra properties which does not exist in 'typeahead' component

    /**
    * @property cssClass - extra css class
    */
    cssClass?: string;

    /**
    * @property overflowHeight - overflow height;
    */
    overflowHeight?: string;
}

export class Typeahead extends TypeaheadApi {
    private _options: TypeaheadOptions;
    private _itemTemplate = '<li><a class="dropdown-item" href="javascript: void(0);" role="option"></a></li>';

    constructor(id: string, options: TypeaheadOptions) {
        super(id);
        this._options = options;
        this.getTypeahead(true);
    }

    protected getInitOptions(): Object {
        var o = this._options;
        var css = StringUtils.isNullOrEmpty(o.cssClass) ? '' : o.cssClass;
        var style = StringUtils.isNullOrEmpty(o.overflowHeight) ? '' : `overflow: auto; height: auto; max-height: ${o.overflowHeight};`;
        var menu = `<ul class="typeahead dropdown-menu${css}" role="listbox" style="${style}"></ul>`;

        return {
            menu: menu,
            item: this._itemTemplate,
            afterSelect: o.afterSelect,
            autoSelect: o.autoSelect,
            delay: o.delay,
            displayText: o.displayText,
            fitToElement: o.fitToElement,
            items: o.items,
            minLength: o.minLength,
            scrollHeight: o.scrollHeight,
            showHintOnFocus: o.showHintOnFocus,
            source: o.source
        };
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Widgets/Typeahead.ts
 **/